import React, { useEffect, useState, useCallback } from 'react';
import { CreditCard, MapPin, Plus, RefreshCw, Trash2, Check, X, AlertTriangle, ChevronDown } from 'lucide-react';
import { api } from '../../services/apiService';
import { RubyLoader } from '../RubyLoader';
import { MagtekReadinessChips } from './unigate/MagtekReadinessChips';
import type { MagtekReadiness } from './unigate/unigateCreds';

import { RubyGemMark } from '../RubyGemMark';
interface Processor {
  deviceId: string;
  deviceName: string;
  locationId: string;
  tpnMasked?: string | null;
  hasCreds?: boolean;
  hasAuthToken?: boolean;
  status: string;
  kind?: 'dejavoo' | 'magtek';
  readiness?: MagtekReadiness;
}
interface OnlineTerm { terminalId: string; locationId: string; locationName?: string; emvDeviceId?: string | null; }
interface Loc { locationId: string; name: string; }

interface Props {
  terminal: { terminalId: string; locationId: string; locationName?: string; registerName: string; emvDeviceId?: string | null };
  tenantId: string;
}

/**
 * "Online Payment" tab — the Dejavoo Cloud PROCESSOR POOL for online sales.
 *
 * A processor (TPN) can serve the online storefronts of MULTIPLE locations. Each
 * location's online terminal references one processor; assigning a processor to a
 * location points that location's online terminal at it (creating one if the
 * location has no online terminal yet). This tab lets you create processors and
 * manage which locations use each — including the current terminal's location.
 */
const PosOnlineTerminalsTab: React.FC<Props> = ({ terminal, tenantId }) => {
  const [processors, setProcessors] = useState<Processor[]>([]);
  const [onlineTerms, setOnlineTerms] = useState<OnlineTerm[]>([]);
  const [locations, setLocations] = useState<Loc[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [form, setForm] = useState({ deviceName: '', spinTpn: '', iposAuthToken: '', iposApiKey: '', iposSecretKey: '' });
  const [confirmDel, setConfirmDel] = useState<Processor | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [openAssignFor, setOpenAssignFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [procRes, termRes, locRes] = await Promise.all([
        api.posNmi.listOnlineProcessors(tenantId),
        api.posTerminals.list(tenantId).catch(() => ({ terminals: [] })),
        api.locationBilling.listTenantRequests(tenantId).catch(() => ({ requests: [] })),
      ]);
      setProcessors(procRes.processors || []);
      setOnlineTerms(
        ((termRes as any)?.terminals || [])
          .filter((t: any) => t.terminalType === 'online_sales')
          .map((t: any): OnlineTerm => ({ terminalId: t.terminalId, locationId: t.locationId, locationName: t.locationName, emvDeviceId: t.emvDeviceId ?? null })),
      );
      setLocations(
        ((locRes as any)?.requests || [])
          .filter((r: any) => r.status === 'active' && r.locationId)
          .map((r: any): Loc => ({ locationId: r.locationId, name: r.name || r.locationId })),
      );
    } catch (e: any) {
      setMsg({ type: 'err', text: e?.message || 'Failed to load processors' });
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const locName = (id: string) => locations.find((l) => l.locationId === id)?.name || onlineTerms.find((t) => t.locationId === id)?.locationName || id;
  const maskFor = (deviceId?: string | null) => (deviceId ? (processors.find((p) => p.deviceId === deviceId)?.tpnMasked || '••••') : null);

  // Locations whose online terminal currently references this processor (deduped).
  const locationsUsing = (deviceId: string): Loc[] => {
    const ids = new Set(onlineTerms.filter((t) => t.emvDeviceId === deviceId).map((t) => t.locationId));
    return Array.from(ids).map((id) => ({ locationId: id, name: locName(id) }));
  };
  // The processor a given location's online terminal currently uses (if any).
  const processorAtLocation = (locationId: string): string | null =>
    onlineTerms.find((t) => t.locationId === locationId && t.emvDeviceId)?.emvDeviceId || null;
  // The register we act on for a location — the current terminal for its own
  // location, else that location's existing online terminal (undefined → create).
  const terminalForLocation = (locationId: string): string | undefined =>
    locationId === terminal.locationId ? terminal.terminalId : onlineTerms.find((t) => t.locationId === locationId)?.terminalId;

  const assignToLocation = async (p: Processor, locationId: string) => {
    setBusy(true);
    try {
      const regId = terminalForLocation(locationId);
      if (regId) {
        await api.posNmi.updateOnlineTerminal(tenantId, regId, { emvDeviceId: p.deviceId });
      } else {
        await api.posNmi.createOnlineTerminal(tenantId, { locationId, locationName: locName(locationId), name: 'Online Store', emvDeviceId: p.deviceId });
      }
      setOpenAssignFor(null);
      await load();
      setMsg({ type: 'ok', text: `"${p.deviceName}" assigned to ${locName(locationId)}` });
    } catch (e: any) {
      setMsg({ type: 'err', text: e?.message || 'Assign failed' });
    } finally {
      setBusy(false);
    }
  };

  const unassignLocation = async (p: Processor, locationId: string) => {
    setBusy(true);
    try {
      // Unassign every online terminal at this location that uses this processor.
      const regs = onlineTerms.filter((t) => t.locationId === locationId && t.emvDeviceId === p.deviceId);
      for (const r of regs) {
        await api.posNmi.updateOnlineTerminal(tenantId, r.terminalId, { emvDeviceId: null });
      }
      await load();
      setMsg({ type: 'ok', text: `"${p.deviceName}" removed from ${locName(locationId)}` });
    } catch (e: any) {
      setMsg({ type: 'err', text: e?.message || 'Remove failed' });
    } finally {
      setBusy(false);
    }
  };

  const addProcessor = async () => {
    if (!form.deviceName.trim() || !form.spinTpn.trim() || !form.iposAuthToken.trim()) {
      setMsg({ type: 'err', text: 'Nickname, TPN and Auth Token are required' });
      return;
    }
    setBusy(true);
    try {
      await api.posNmi.registerCloudProcessor(tenantId, terminal.locationId, {
        deviceName: form.deviceName.trim(), spinTpn: form.spinTpn.trim(), iposAuthToken: form.iposAuthToken.trim(),
        iposApiKey: form.iposApiKey.trim() || undefined, iposSecretKey: form.iposSecretKey.trim() || undefined,
      });
      setShowAdd(false);
      setForm({ deviceName: '', spinTpn: '', iposAuthToken: '', iposApiKey: '', iposSecretKey: '' });
      await load();
      setMsg({ type: 'ok', text: 'Processor added to the pool' });
    } catch (e: any) {
      setMsg({ type: 'err', text: e?.message || 'Could not add processor' });
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (p: Processor) => {
    setDeleting(true);
    try {
      await api.posNmi.deleteProcessor(tenantId, p.deviceId);
      setConfirmDel(null);
      await load();
      setMsg({ type: 'ok', text: `Deleted "${p.deviceName}"` });
    } catch (e: any) {
      setConfirmDel(null);
      setMsg({ type: 'err', text: e?.message || 'Could not delete processor' });
    } finally {
      setDeleting(false);
    }
  };

  const inputCls = 'rounded-lg px-3.5 py-2.5 text-sm bg-white text-stone-900 placeholder:text-stone-400 ring-1 ring-stone-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/40';

  return (
    <div className="max-w-4xl">
      {msg && (
        <div className={`flex items-start gap-2.5 px-4 py-3 rounded-xl mb-5 text-sm ring-1 ${msg.type === 'ok' ? 'bg-emerald-50 text-emerald-800 ring-emerald-200' : 'bg-red-50 text-red-700 ring-red-200'}`}>
          {msg.type === 'ok' ? <Check size={16} className="mt-px shrink-0" /> : <AlertTriangle size={16} className="mt-px shrink-0" />}
          <span className="flex-1 leading-snug">{msg.text}</span>
          <button onClick={() => setMsg(null)} className="opacity-60 hover:opacity-100"><X size={15} /></button>
        </div>
      )}

      <div className="bg-white rounded-2xl ring-1 ring-stone-200/70 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-stone-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100 flex items-center justify-center shrink-0"><CreditCard size={18} /></div>
            <div className="min-w-0">
              <h2 className="text-stone-900 font-semibold text-[15px] leading-tight">Online Payment Processors</h2>
              <p className="text-stone-500 text-xs mt-0.5">Dejavoo Cloud TPNs and MagTek — one processor can serve the storefronts of multiple locations</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={load} className="w-9 h-9 rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-600 flex items-center justify-center transition-colors" title="Refresh">{loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw size={16} />}</button>
            <button onClick={() => setShowAdd((v) => !v)} className="h-9 px-3.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 inline-flex items-center gap-1.5 whitespace-nowrap transition-colors"><Plus size={16} /> Add processor</button>
          </div>
        </div>

        <div className="p-6">
          {showAdd && (
            <div className="bg-stone-50/60 ring-1 ring-stone-200 rounded-xl p-4 mb-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={form.deviceName} onChange={(e) => setForm({ ...form, deviceName: e.target.value })} placeholder="Nickname" className={inputCls} />
                <input value={form.spinTpn} onChange={(e) => setForm({ ...form, spinTpn: e.target.value })} placeholder="Dejavoo TPN" className={`${inputCls} font-mono`} />
                <input value={form.iposApiKey} onChange={(e) => setForm({ ...form, iposApiKey: e.target.value })} placeholder="API key (optional)" className={inputCls} />
                <input value={form.iposSecretKey} onChange={(e) => setForm({ ...form, iposSecretKey: e.target.value })} placeholder="Secret key (optional)" type="password" className={inputCls} />
              </div>
              <div className="mt-3">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Auth Token <span className="text-red-500 normal-case">*required</span></label>
                <textarea value={form.iposAuthToken} onChange={(e) => setForm({ ...form, iposAuthToken: e.target.value })} rows={2} placeholder="Paste the Auth Token from this TPN's Integration page in iPOSpays" className={`w-full ${inputCls} text-xs font-mono break-all resize-none`} />
              </div>
              <p className="text-stone-500 text-[11px] mt-2.5 leading-relaxed">Home location = <span className="font-semibold text-stone-600">{terminal.locationName || 'this terminal’s location'}</span>. The <span className="font-semibold text-stone-600">Auth Token</span> is the storefront card-form key. API/secret keys are optional and fall back to tenant-wide settings.</p>
              <div className="flex justify-end gap-2 mt-3">
                <button disabled={busy} onClick={() => { setShowAdd(false); setForm({ deviceName: '', spinTpn: '', iposAuthToken: '', iposApiKey: '', iposSecretKey: '' }); }} className="h-9 px-4 rounded-lg ring-1 ring-stone-200 bg-white text-stone-700 text-sm font-medium hover:bg-stone-50 disabled:opacity-50 transition-colors">Cancel</button>
                <button disabled={busy} onClick={addProcessor} className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 inline-flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50 transition-colors">{busy && <RubyGemMark size={14} drawing color="currentColor" />} Add processor</button>
              </div>
            </div>
          )}

          {loading ? (
            <RubyLoader variant="section" label="Loading…" className="py-10" />
          ) : processors.length === 0 ? (
            <div className="rounded-2xl ring-1 ring-dashed ring-stone-300 bg-stone-50/50 p-8 text-center">
              <div className="w-12 h-12 rounded-2xl bg-white ring-1 ring-stone-200 flex items-center justify-center mx-auto mb-3"><CreditCard size={20} className="text-stone-400" /></div>
              <p className="text-stone-700 text-sm font-medium">No processors yet</p>
              <p className="text-stone-400 text-xs mt-1 mb-4">Add a Dejavoo Cloud processor, then assign it to one or more locations.</p>
              <button onClick={() => setShowAdd(true)} className="h-10 px-5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 inline-flex items-center gap-2 transition-colors"><Plus size={16} /> Add processor</button>
            </div>
          ) : (
            <div className="space-y-3">
              {processors.map((p) => {
                const usedAt = locationsUsing(p.deviceId);
                const usedIds = new Set(usedAt.map((l) => l.locationId));
                const open = openAssignFor === p.deviceId;
                return (
                  <div key={p.deviceId} className="rounded-2xl ring-1 ring-stone-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-xl bg-stone-900 text-white flex items-center justify-center shrink-0"><CreditCard size={18} /></div>
                        <div className="min-w-0">
                          {p.kind === 'magtek' ? (
                            <>
                              <div className="text-lg font-bold text-stone-900 leading-none">MagTek</div>
                              <div className="text-stone-500 text-xs mt-1 truncate">"{p.deviceName}"</div>
                              <div className="mt-1.5"><MagtekReadinessChips readiness={p.readiness} /></div>
                            </>
                          ) : (
                            <>
                              <div className="font-mono text-lg font-bold tracking-wider text-stone-900 tabular-nums leading-none">{p.tpnMasked || '—'}</div>
                              <div className="text-stone-500 text-xs mt-1 truncate">"{p.deviceName}"{!p.hasAuthToken ? ' · ⚠ auth token needed' : ''}</div>
                            </>
                          )}
                        </div>
                      </div>
                      {/* MagTek online processors are created per location with its
                          register in one step; there is no assign / delete flow for them. */}
                      {p.kind !== 'magtek' && (
                        <button disabled={busy} onClick={() => setConfirmDel(p)} title="Delete this processor" className="h-8 w-8 shrink-0 rounded-lg ring-1 ring-red-200 text-red-500 bg-white inline-flex items-center justify-center hover:bg-red-50 disabled:opacity-50 transition-colors"><Trash2 size={14} /></button>
                      )}
                    </div>

                    <div className="mt-3.5 pt-3.5 border-t border-stone-100">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 mb-2">Assigned at</div>
                      {usedAt.length === 0 ? (
                        <p className="text-stone-400 text-xs mb-2.5">Not assigned to any location yet.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2 mb-2.5">
                          {usedAt.map((l) => {
                            const here = l.locationId === terminal.locationId;
                            return (
                              <span key={l.locationId} className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full pl-2.5 pr-1.5 py-1 ring-1 ${here ? 'bg-emerald-50 ring-emerald-200 text-emerald-700' : 'bg-stone-50 ring-stone-200 text-stone-600'}`}>
                                <MapPin size={11} /> {l.name}{here && <span className="text-emerald-500">· this location</span>}
                                <button disabled={busy} onClick={() => unassignLocation(p, l.locationId)} title={`Remove from ${l.name}`} className="ml-0.5 w-4 h-4 rounded-full hover:bg-black/10 inline-flex items-center justify-center disabled:opacity-50"><X size={11} /></button>
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {p.kind !== 'magtek' && (
                      <div className="relative">
                        <button disabled={busy} onClick={() => setOpenAssignFor(open ? null : p.deviceId)} className="h-8 px-3 rounded-lg ring-1 ring-stone-200 bg-white text-stone-700 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-stone-50 disabled:opacity-50 transition-colors">
                          <Plus size={13} /> Assign to location <ChevronDown size={13} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
                        </button>
                        {open && (
                          <div className="mt-2 rounded-xl ring-1 ring-stone-200 bg-white shadow-sm overflow-hidden max-w-md">
                            {locations.length === 0 && <div className="px-3 py-2.5 text-stone-400 text-xs">No active locations found.</div>}
                            {locations.map((loc) => {
                              const already = usedIds.has(loc.locationId);
                              const otherProc = !already ? processorAtLocation(loc.locationId) : null;
                              return (
                                <button key={loc.locationId} disabled={already || busy} onClick={() => assignToLocation(p, loc.locationId)}
                                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm border-b border-stone-100 last:border-0 ${already ? 'text-stone-400 cursor-default' : 'text-stone-700 hover:bg-emerald-50/50'}`}>
                                  <MapPin size={13} className="shrink-0 text-stone-400" />
                                  <span className="flex-1 min-w-0 truncate">{loc.name}</span>
                                  {already ? (
                                    <span className="text-[11px] font-semibold text-emerald-600 inline-flex items-center gap-1 shrink-0"><Check size={12} /> assigned</span>
                                  ) : otherProc ? (
                                    <span className="text-[11px] text-amber-600 shrink-0">switch from <span className="font-mono">{maskFor(otherProc)}</span></span>
                                  ) : (
                                    <span className="text-[11px] text-emerald-600 font-semibold shrink-0">assign →</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 p-4" onClick={() => !deleting && setConfirmDel(null)}>
          <div className="bg-white rounded-2xl ring-1 ring-stone-200 shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-11 h-11 rounded-xl bg-red-50 text-red-600 ring-1 ring-red-100 flex items-center justify-center mb-3"><Trash2 size={19} /></div>
            <h3 className="text-stone-900 font-semibold text-[15px]">Delete this processor?</h3>
            <p className="text-stone-500 text-sm mt-1.5 leading-relaxed">
              <span className="font-mono font-semibold text-stone-700">{confirmDel.tpnMasked || '—'}</span> · "{confirmDel.deviceName}" will be permanently removed. If it's still assigned to any location, remove it there first.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button disabled={deleting} onClick={() => setConfirmDel(null)} className="h-9 px-4 rounded-lg ring-1 ring-stone-200 bg-white text-stone-700 text-sm font-medium hover:bg-stone-50 disabled:opacity-50 transition-colors">Cancel</button>
              <button disabled={deleting} onClick={() => doDelete(confirmDel)} className="h-9 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors">
                {deleting ? <><RubyGemMark size={14} drawing color="currentColor" /> Deleting…</> : <><Trash2 size={14} /> Delete</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PosOnlineTerminalsTab;
